import { useState, useEffect } from 'react';
import { X, Share, Plus, Download, Smartphone, Sparkles, Bell, Heart } from 'lucide-react';

const DISMISS_KEY = 'salaam_install_dismissed_v1';
const DISMISS_DAYS = 7; // re-prompt after 7 days if user dismisses

/**
 * Smart PWA install banner.
 * - On Chrome/Android/Edge: captures beforeinstallprompt and offers a direct install button.
 * - On iOS Safari: shows the manual "Share → Add to Home Screen" instructions with icons.
 * - Auto-hides when already running as standalone (installed).
 */
export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState('unknown'); // ios | android-chrome | other

  useEffect(() => {
    // Don't show if already in standalone (installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    // Don't show if recently dismissed
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const dismissedAt = parseInt(raw, 10);
        if (Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
      }
    } catch (e) { /* ignore */ }

    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);

    if (isIOS && isSafari) {
      setPlatform('ios');
      // Delay 3s so it doesn't interrupt loading
      const t = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(t);
    }

    // Chrome/Edge/Samsung/etc. — listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform('android-chrome');
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice && choice.outcome === 'accepted') {
      setVisible(false);
    } else {
      dismiss();
    }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" data-testid="install-prompt">
      <div className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="relative px-5 py-5 text-center" style={{ background: 'linear-gradient(135deg, #8b7ff0 0%, #a5a5ea 50%, #7dd3c3 100%)' }}>
          <button onClick={dismiss} className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white/25 hover:bg-white/40 flex items-center justify-center backdrop-blur" data-testid="install-close-btn">
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="w-16 h-16 mx-auto rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center mb-2">
            <Smartphone className="w-7 h-7 text-white" strokeWidth={2.2} />
          </div>
          <div className="text-white text-[16px] font-black mb-1">أضيفي سلام إلى شاشتك</div>
          <div className="text-white/85 text-[11.5px]">افتحيها بلمسة واحدة كأنه تطبيق كامل</div>
        </div>

        {/* Benefits */}
        <div className="px-5 py-4 space-y-2.5">
          <Benefit icon={Sparkles} text="فتح فوري بلا شريط متصفح" color="#8b7ff0" bg="var(--primary-bg)" />
          <Benefit icon={Bell} text="إشعارات الأدوية تصلك حتى والتطبيق مغلق" color="#5eb2a3" bg="var(--secondary-bg)" />
          <Benefit icon={Heart} text="أيقونة الحمامة على شاشتك الرئيسية" color="#d17c53" bg="rgba(240,177,108,0.15)" />

          {platform === 'ios' ? (
            <div className="rounded-2xl p-3.5 mt-3" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)' }}>
              <div className="text-[11.5px] font-extrabold mb-2.5 text-center" style={{ color: 'var(--text-2)' }}>خطوتان فقط:</div>
              <div className="flex items-center gap-2.5 mb-2.5">
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[11px] font-black" style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }}>1</div>
                <div className="text-[12px] leading-relaxed" style={{ color: 'var(--text)' }}>
                  اضغطي زر المشاركة <span className="inline-flex items-center justify-center w-6 h-6 rounded-md align-middle mx-0.5" style={{ background: 'var(--primary-bg)' }}><Share className="w-3 h-3" style={{ color: 'var(--primary-dark)' }} /></span> في الأسفل
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[11px] font-black" style={{ background: 'linear-gradient(135deg, #5eb2a3, #3f8579)' }}>2</div>
                <div className="text-[12px] leading-relaxed" style={{ color: 'var(--text)' }}>
                  اختاري <span className="font-extrabold">&quot;إضافة إلى الشاشة الرئيسية&quot;</span> <span className="inline-flex items-center justify-center w-6 h-6 rounded-md align-middle mx-0.5" style={{ background: 'var(--secondary-bg)' }}><Plus className="w-3 h-3" style={{ color: 'var(--secondary-dark)' }} /></span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex gap-2">
          <button onClick={dismiss} className="flex-1 py-3 rounded-2xl text-[12.5px] font-extrabold border-2 transition-colors" style={{ borderColor: 'var(--border)', color: 'var(--text-2)' }} data-testid="install-later-btn">
            لاحقاً
          </button>
          {platform === 'android-chrome' ? (
            <button onClick={install} className="flex-1 py-3 rounded-2xl text-white text-[12.5px] font-extrabold shadow-md flex items-center justify-center gap-2 active:scale-95 transition-transform" style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }} data-testid="install-now-btn">
              <Download className="w-4 h-4" />
              تثبيت الآن
            </button>
          ) : (
            <button onClick={dismiss} className="flex-1 py-3 rounded-2xl text-white text-[12.5px] font-extrabold shadow-md" style={{ background: 'linear-gradient(135deg, #8b7ff0, #6a5cd8)' }} data-testid="install-got-it-btn">
              فهمت
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Benefit({ icon: Icon, text, color, bg }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="text-[12px] leading-relaxed" style={{ color: 'var(--text)' }}>{text}</div>
    </div>
  );
}
